/**
 * Interface translation (2026-10-16, QA #4b).
 *
 * Gettext-style: the English sentence IS the key. `t('Save')` returns the
 * active catalogue's entry for "Save", or "Save" itself when there is none —
 * so a string nobody has translated yet still reads, and English needs no
 * catalogue at all. Catalogues live in `locales/<lang>.json` and are loaded
 * on demand, one language per session.
 *
 * Rules for callers (the catalogue test enforces what it can):
 * - Call `t()` at render time, never at module top level. Modules evaluate
 *   before `initI18n()` has loaded a catalogue, so a top-level `t()` would
 *   freeze the English. Keep English in constants; translate where it is drawn.
 * - The first argument must be a string literal (or a constant holding one
 *   that is also passed through `t()` somewhere as a literal) — the
 *   extraction in `i18n-catalog.test.ts` finds keys by reading the source.
 * - Variables go in `{braces}`: `t('Level {n}', { n: 3 })`. Never build a
 *   sentence by concatenating translated halves; word order differs.
 * - Counts: `tp(n, 'One run left', '{n} runs left')`. Russian and Polish have
 *   more than two plural forms; the catalogue holds them by
 *   Intl.PluralRules category.
 * - Same English, different meaning: `tc('gear slot', 'Back')` vs `t('Back')`
 *   (the button). The catalogue key is `context + '\u0005' + text`; English
 *   still shows `text`. `NC_(context, text)` marks one for a data table, drawn
 *   with `tc(context, value)`. The context is a plain literal, no {braces}.
 *
 * Not covered: core's auth and payment screens (frontend/src/core — template
 * code a game repo may not edit) and the admin panel, which stays English.
 */

export const LANGS = ['en', 'et', 'de', 'fr', 'es', 'pl', 'ru'] as const;
export type Lang = (typeof LANGS)[number];

/** A plural entry: one translation per Intl.PluralRules category. */
export type PluralEntry = Partial<Record<Intl.LDMLPluralRule, string>>;
export type Catalogue = Record<string, string | PluralEntry>;

const STORAGE_KEY = 'tallow.locale';

/** Separates the singular and plural English of a `tp()` key. */
export const PLURAL_SEP = '\u0004';

/** Separates the disambiguating context from the English of a `tc()` key. */
export const CONTEXT_SEP = '\u0005';

const loaders = import.meta.glob<Catalogue>('./locales/*.json', { import: 'default' });

let lang: Lang = 'en';
let catalogue: Catalogue = {};
let plural = new Intl.PluralRules('en');

/** `ru_RU`, `ru-RU`, `ru` → `ru`; anything unsupported → null. */
export function langFromTag(tag: string | null | undefined): Lang | null {
  const base = (tag ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return (LANGS as readonly string[]).includes(base) ? (base as Lang) : null;
}

/** The account setting wins; blank means "match my browser". */
export function resolveLang(setting: string | null | undefined, browser: readonly string[] = navigatorLanguages()): Lang {
  const chosen = langFromTag(setting);
  if (chosen) return chosen;
  for (const tag of browser) {
    const l = langFromTag(tag);
    if (l) return l;
  }
  return 'en';
}

function navigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

export function getLang(): Lang {
  return lang;
}

/** BCP 47 tag for Intl formatting of numbers and dates in the active language. */
export function getIntlLocale(): string {
  return lang === 'en' ? 'en-GB' : lang;
}

/**
 * A number in the active language's notation: `1.25` reads `1,25` in de/fr/ru.
 * `digits` fixes the decimals (`toFixed`'s job); without it up to two show.
 */
export function formatNumber(n: number, digits?: number): string {
  return n.toLocaleString(getIntlLocale(), digits === undefined
    ? { maximumFractionDigits: 2 }
    : { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

/**
 * Marks a literal for translation without translating it — for data tables
 * built at module load (item names, talent copy). The table keeps English;
 * the screen that draws it calls `t(def.name)`.
 */
export const N_ = (text: string): string => text;

/** `N_()` for a context-qualified string: draw it with `tc(context, value)`. */
export const NC_ = (_context: string, text: string): string => text;

export function t(text: string, vars?: Record<string, string | number>): string {
  const hit = catalogue[text];
  return interpolate(typeof hit === 'string' && hit ? hit : text, vars);
}

/**
 * `t()` for English that means two different things — "Back" the button and
 * "Back" the gear slot — so each meaning gets its own catalogue entry.
 */
export function tc(context: string, text: string, vars?: Record<string, string | number>): string {
  const hit = catalogue[context + CONTEXT_SEP + text];
  return interpolate(typeof hit === 'string' && hit ? hit : text, vars);
}

/** Count-aware `t()`. `{n}` is filled with `n` unless `vars` says otherwise. */
export function tp(n: number, one: string, other: string, vars?: Record<string, string | number>): string {
  const all = { n, ...vars };
  const entry = catalogue[one + PLURAL_SEP + other];
  const category = plural.select(n);
  if (entry && typeof entry === 'object') {
    const form = entry[category] ?? entry.other;
    if (form) return interpolate(form, all);
  }
  return interpolate(n === 1 ? one : other, all);
}

/** Swap the active language. Unknown or failed catalogues fall back to English. */
export async function setLang(next: Lang): Promise<void> {
  let loaded: Catalogue = {};
  if (next !== 'en') {
    const load = loaders[`./locales/${next}.json`];
    try {
      loaded = load ? await load() : {};
    } catch {
      loaded = {};
    }
  }
  lang = next;
  catalogue = loaded;
  plural = new Intl.PluralRules(next);
  if (typeof document !== 'undefined') document.documentElement.lang = next;
}

/** The saved account locale, mirrored here so boot does not wait on the API. */
export function rememberLocaleSetting(setting: string | null | undefined): void {
  try {
    if (setting) localStorage.setItem(STORAGE_KEY, setting);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode: the browser language is used next boot */ }
}

/**
 * Whether `setting` is what the next boot will read back. False with storage
 * blocked (private mode, site data off): a reload would then boot in the
 * browser language, so callers redraw in memory instead.
 */
export function localePersisted(setting: string | null | undefined): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return setting ? stored === setting : stored === null;
  } catch {
    return false;
  }
}

/**
 * Window event asking the mounted session to redraw itself in the language
 * now active, without a reload (see `localePersisted`).
 */
export const RELANGUAGE_EVENT = 'tallow:relanguage';

function storedLocaleSetting(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Boot: before the first screen renders. */
export async function initI18n(): Promise<void> {
  await setLang(resolveLang(storedLocaleSetting()));
}

/**
 * The account's saved locale arrived (settings overview, or after login).
 * Returns true when it changed the language, so the caller can re-render.
 */
export async function adoptLocaleSetting(setting: string | null | undefined): Promise<boolean> {
  rememberLocaleSetting(setting);
  const next = resolveLang(setting);
  if (next === lang) return false;
  await setLang(next);
  return true;
}

/** Test seam: install a catalogue without the loader. */
export function __setCatalogueForTest(next: Lang, cat: Catalogue): void {
  lang = next;
  catalogue = cat;
  plural = new Intl.PluralRules(next);
}
